// MendixPage.jsx
// Fully aligned with PLMSolutionsAMCPage structure and styling.
// Keeps Mendix-specific content, icons, and sections intact.

import React, { useState } from "react";
import "./MendixPage.css";
import MendixOverview from "./MendixOverview";
import BannerImage from "../../../assets/other_image/mendix.jpg";
import Letsgetintouch from "../../PLMImplementation/BusinessProcessAndChangeManagement/Letsgetintouch";
import Banner from "../SolutionBanner/Banner";
import FloatingContactUs from "../../floting_contact/FloatingContactUs";
import BookDemoSection from "../PolarianALMSolution/BookDemoSection";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/* -----------------------
   FAQ data (Mendix)
   ----------------------- */
const faqData = [
  { question: "What is Mendix?", answer: "Mendix is a low-code application development platform that enables businesses to build, deploy, and manage web and mobile apps quickly and efficiently." },
  { question: "Can Mendix handle enterprise-grade projects?", answer: "Yes. Mendix supports cloud-native deployment, scalability, and integration with major enterprise systems like ERP and CRM." },
  { question: "Does Mendix support mobile apps?", answer: "Absolutely. Mendix’s React Native framework allows developers to create native iOS and Android applications from a single codebase." },
  { question: "How does Mendix fit into Agile and DevOps?", answer: "Mendix integrates seamlessly with Agile workflows and DevOps pipelines, offering built-in tools for sprint planning, CI/CD automation, and testing." },
  { question: "Can Mendix integrate with our current systems?", answer: "Yes. Mendix includes pre-built connectors and APIs for ERP, CRM, and other custom business applications." },
  { question: "How long does it take to implement Mendix?", answer: "Timelines vary — simple apps can be created in a few days, while complex enterprise solutions may take several weeks." },
  { question: "Is Mendix accessible?", answer: "Yes. Mendix complies with WCAG 2.1 accessibility standards, ensuring your applications are inclusive and user-friendly for everyone." }
];

/* FAQ Component */
const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button className="faq-question" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span>{question}</span>
        <span>{open ? "−" : "+"}</span>
      </button>
      {open && <div className="faq-answer">{answer}</div>}
    </div>
  );
};

/* Section Header Component */
const SectionHeader = ({ title, subtitle }) => (
  <header className="section-header">
    <h2 className="section-title">{title}</h2>
    {subtitle && <p className="section-subtitle">{subtitle}</p>}
  </header>
);

/* Tile Grid Component */
const TileGrid = ({ items }) => (
  <div className="tile-grid-3x2">
    {items.map((item, index) => (
      <article className="feature-tile" key={index}>
        {item.icon && <div className="tile-icon">{item.icon}</div>}
        <h3 className="tile-title">{item.title}</h3>
        <p className="tile-content">{item.content}</p>
      </article>
    ))}
  </div>
);

/* Icon helper (icons8) */
const icon = (path, alt = "") => (
  <img className="tile-icon-img" src={path} alt={alt} width="48" height="48" loading="lazy" />
);

const MendixPage = () => {
  /* Why Nextech Tiles */
  const whyNextechItems = [
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/flash-on.png", "agile"), title: "Agile & DevOps Expertise", content: "Accelerate delivery cycles with Agile methodologies and DevOps automation that improve quality and reduce errors." },
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/handshake.png", "alignment"), title: "Business–IT Alignment", content: "Bridge the gap between business and technical teams, ensuring stakeholder engagement throughout development." },
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/wrench.png", "platform-knowledge"), title: "Unified Platform Knowledge", content: "Leverage our deep understanding of no-code and low-code capabilities to create flexible, scalable applications." },
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/cloud.png", "cloud"), title: "Cloud-Native Deployment", content: "Utilize cloud-native architecture for automated scaling, resilience, and deployment in any environment." },
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/smartphone.png", "cross-platform"), title: "Cross-Platform Delivery", content: "Build once and deploy seamlessly to iOS, Android, and web without extra development overhead." },
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/target.png", "ux"), title: "UX & Accessibility Leadership", content: "Deliver engaging, WCAG-compliant digital experiences across all devices." }
  ];

  /* Featured Solutions */
  const featuredSolutionsItems = [
    { title: "No-Code & Low-Code Development", content: "Empower both developers and business users to create applications quickly — without compromising control or security." },
    { title: "Agile & DevOps Enablement", content: "Implement continuous delivery, automated testing, and rapid iteration for smooth project execution." },
    { title: "Cross-Platform Mobile Development", content: "Develop native iOS and Android apps from a single codebase using Mendix’s React Native framework." },
    { title: "Cloud-Native Architecture", content: "Deploy and manage applications on any cloud environment with built-in scalability and high availability." },
    { title: "Business–IT Collaboration Tools", content: "Bridge the gap between technical teams and business stakeholders with real-time collaboration and version control." },
    { title: "Responsive & Accessible Design", content: "Deliver user-friendly experiences across all devices while meeting WCAG 2.1 accessibility standards." }
  ];

  /* Our Solutions */
  const ourSolutionsItems = [
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/rocket.png", "rocket"), title: "Complete Mendix Implementation", content: "Launch high-quality Mendix applications quickly with a customized setup designed around your business needs." },
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/link.png", "integration"), title: "Enterprise System Integration", content: "Connect Mendix seamlessly with ERP, CRM, and legacy systems for efficient data flow." },
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/paint-palette.png", "ui"), title: "Custom UI & Workflow Design", content: "Create user-friendly interfaces and optimized workflows reflecting your brand identity." },
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/books.png", "training"), title: "Role-Based Training", content: "Empower your team with hands-on Mendix training tailored to specific roles." },
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/settings.png", "process"), title: "Process Optimization", content: "Streamline development cycles by aligning projects with business objectives." },
    { icon: icon("https://img.icons8.com/ios-filled/48/07b6f2/shield.png", "support"), title: "Managed Support", content: "Ensure long-term success with proactive maintenance and scalability enhancements." }
  ];

  return (
    <>
      {/* ✅ Meta Tags for SEO */}
    <Helmet>
      <title>Mendix Apps &amp; Solutions Services in Pune | NexTech PLM</title>
      <meta name="description" content="Build and deploy custom Mendix applications with Nextech PLM in Pune, India. Accelerate digital transformation for your business with low-code development." />
      <meta name="keywords" content="Mendix app development Pune, low-code platform India, Mendix solutions, custom Mendix applications, digital transformation low-code" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href="https://nextechplm.com/mendix-apps-solutions" />
      <meta property="og:site_name" content="NexTech PLM Solutions" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Mendix Apps & Solutions Services in Pune | NexTech PLM" />
      <meta property="og:description" content="Build and deploy custom Mendix applications with Nextech PLM in Pune, India. Accelerate digital transformation for your business with low-code development." />
      <meta property="og:url" content="https://nextechplm.com/mendix-apps-solutions" />
      <meta property="og:image" content="https://nextechplm.com/logo192.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Mendix Apps & Solutions Services in Pune | NexTech PLM" />
      <meta name="twitter:description" content="Build and deploy custom Mendix applications with Nextech PLM in Pune, India. Accelerate digital transformation for your business with low-code development." />
      <meta name="twitter:image" content="https://nextechplm.com/logo192.png" />
</Helmet>

    <main className="mendix-solution-page">
      <Banner image={BannerImage} heading="Mendix Solutions" />
      <FloatingContactUs />
      <MendixOverview />

      {/* Benefits & Quick Links Section */}
      <section className="section benefits-section">
        <div className="container">
          <div className="row g-4 align-items-center">
            <div className="col-md-6 col-12">
              <div className="benefits-card">
                <h3 className="benefits-heading">Business Benefits of Mendix with NexTech</h3>
                <ul className="benefits-list">
                  <li><span className="benefit-icon">✔</span> Accelerate app development with low-code, reducing time-to-market.</li>
                  <li><span className="benefit-icon">✔</span> Seamless collaboration between business and IT stakeholders.</li>
                  <li><span className="benefit-icon">✔</span> Scalable, cloud-ready Mendix deployments.</li>
                  <li><span className="benefit-icon">✔</span> Maximize ROI through rapid prototyping and iterative delivery.</li>
                  <li><span className="benefit-icon">✔</span> Enhance user experience with modern, adaptive interfaces.</li>
                  <li><span className="benefit-icon">✔</span> Future-proof innovation with AI and automation capabilities.</li>
                </ul>
              </div>
            </div>

            <div className="col-md-6 col-12 d-flex align-items-center">
              <div className="quick-links">
                <h4>Quick Links</h4>
                <ul>
                  <li><NavLink to={"/teamcenter/plm-document-management"}>Document Management</NavLink></li>
                  <li><NavLink to={"/teamcenter/manufacturing-data-process-management"}>Manufacturing Data and Process Management</NavLink></li>
                  <li><NavLink to={"/teamcenter/product-bom-variability-management"}>Product BOM and Variability Management</NavLink></li>
                  <li><NavLink to={"/teamcenter/community-collaboration"}>Teamcenter Community Collaboration</NavLink></li>
                  <li><NavLink to={"/teamcenter/cloud-implementation"}>Teamcenter Implementation on Cloud</NavLink></li>
                  <li><NavLink to={"/teamcenter/ecad-integration"}>ECAD Integration</NavLink></li>
                  <li><NavLink to={"/teamcenter/mcad-integration"}>MCAD Integration</NavLink></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BookDemoSection />

      {/* Featured Solutions */}
      <section className="section featured-solutions-section">
        <div className="container">
          <SectionHeader title="Mendix Featured Solutions" subtitle="NexTech empowers enterprises to innovate faster using Mendix low-code." />
          <TileGrid items={featuredSolutionsItems} />
        </div>
      </section>

      {/* Video promo (left text / right player) */}
      <section className="video-promo">
        <div className="video-promo-container">
          <div className="video-promo-content">
            <h2 className="video-promo-heading">Transform Mendix into a Strategic Business Advantage</h2>
            <p className="video-promo-text">
              At NexTech, we go beyond simple deployment — we turn Mendix into a powerful driver of business growth. 
              Whether you need rapid implementation, enterprise system connections, or ongoing support, we ensure your Mendix investment delivers measurable value.
            </p>
            <ul className="video-promo-features">
              <li className="feature-item"><span className="feature-icon">🚀</span>Faster application delivery through Agile and DevOps practices</li>
              <li className="feature-item"><span className="feature-icon">🔗</span>Seamless enterprise system integrations</li>
              <li className="feature-item"><span className="feature-icon">📈</span>Scalable, future-ready architecture</li>
            </ul>
          </div>

          <div className="video-promo-player">
            <div className="video-wrapper">
              <iframe
                src="https://www.youtube.com/embed/cv8vs0y3YMw?autoplay=0&rel=0&modestbranding=1"
                title="Mendix Solution Overview"
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
            <p className="video-caption">Watch how Mendix can transform your digital strategy</p>
          </div>
        </div>
      </section>

      {/* Our Solutions */}
      <section className="section our-solutions-section">
        <div className="container">
          <SectionHeader title="Our Mendix Services" />
          <TileGrid items={ourSolutionsItems} />
        </div>
      </section>

      {/* Why Nextech */}
      <section className="section why-nextech-section">
        <div className="container">
          <SectionHeader title="Why Choose NexTech for Mendix?" />
          <TileGrid items={whyNextechItems} />
        </div>
      </section>

      {/* FAQ */}
      <section className="section faq-section">
        <div className="container">
          <SectionHeader title="Mendix FAQs" />
          <div className="faq-container">
            {faqData.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      <Letsgetintouch />
    </main>
</>
  );
};

export default MendixPage;
